import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Wallet, 
  TrendingUp, 
  Bot, 
  ChevronRight, 
  CheckCircle2,
  Sparkles,
  Target,
  GraduationCap
} from 'lucide-react';

const ONBOARDING_KEY = 'dse_onboarding_completed';

export default function OnboardingWizard({ isOpen, onClose, portfolioCount = 0 }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    // Check if user has completed onboarding
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed && isOpen) {
      setShowWizard(true);
    }
  }, [isOpen]);

  const steps = [
    {
      icon: Sparkles,
      title_en: "Welcome to Wekeza-Smart!",
      title_sw: "Karibu Wekeza-Smart!",
      description_en: `Hi ${user?.name?.split(' ')[0] || 'there'}! Let's get your investment journey started in under 60 seconds.`,
      description_sw: `Habari ${user?.name?.split(' ')[0] || 'rafiki'}! Hebu tuanze safari yako ya uwekezaji chini ya sekunde 60.`,
      action: null,
      color: "bg-primary"
    },
    {
      icon: Wallet,
      title_en: "Add Your First Investment",
      title_sw: "Ongeza Uwekezaji Wako wa Kwanza",
      description_en: "Track your DSE stocks or UTT funds. Enter what you've already invested to see your portfolio value and returns.",
      description_sw: "Fuatilia hisa zako za DSE au mifuko ya UTT. Ingiza ulichokwisha wekeza ili kuona thamani na faida ya portfolio yako.",
      action: () => navigate('/portfolio/add'),
      actionLabel_en: "Add Investment",
      actionLabel_sw: "Ongeza Uwekezaji",
      color: "bg-emerald-500",
      skipable: portfolioCount > 0
    },
    {
      icon: Target,
      title_en: "Set Your Financial Goals",
      title_sw: "Weka Malengo Yako ya Kifedha",
      description_en: "Whether it's saving for a house, education, or retirement - we'll help you plan and track progress.",
      description_sw: "Iwe ni kuweka akiba ya nyumba, elimu, au kustaafu - tutakusaidia kupanga na kufuatilia maendeleo.",
      action: () => navigate('/goals'),
      actionLabel_en: "Set Goals",
      actionLabel_sw: "Weka Malengo",
      color: "bg-orange-500"
    },
    {
      icon: Bot,
      title_en: "Meet Your AI Coach",
      title_sw: "Kutana na Mshauri Wako wa AI",
      description_en: "Get personalized investment advice, market insights, and answers to your questions - all in Swahili or English.",
      description_sw: "Pata ushauri wa uwekezaji wa kibinafsi, ufahamu wa soko, na majibu ya maswali yako - kwa Kiswahili au Kiingereza.",
      action: () => navigate('/chat'),
      actionLabel_en: "Ask AI Coach",
      actionLabel_sw: "Uliza Mshauri AI",
      color: "bg-blue-500"
    },
    {
      icon: CheckCircle2,
      title_en: "You're All Set!",
      title_sw: "Umekamilisha!",
      description_en: "Your dashboard will show your portfolio value, gains/losses, and AI recommendations. Start exploring!",
      description_sw: "Dashibodi yako itaonyesha thamani ya portfolio, faida/hasara, na mapendekezo ya AI. Anza kuchunguza!",
      action: null,
      color: "bg-primary"
    }
  ];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setShowWizard(false);
    onClose?.();
  };

  const handleAction = (action) => {
    if (action) {
      handleComplete();
      action();
    }
  };

  const currentStep = steps[step];
  const progress = ((step + 1) / steps.length) * 100;

  if (!showWizard) return null;

  return (
    <Dialog open={showWizard} onOpenChange={(open) => !open && handleSkip()}>
      <DialogContent className="sm:max-w-md" data-testid="onboarding-wizard">
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className={`w-16 h-16 rounded-2xl ${currentStep.color} flex items-center justify-center`}>
              <currentStep.icon className="w-8 h-8 text-white" />
            </div>
          </div>
          <DialogTitle className="text-xl">
            {t(currentStep.title_en, currentStep.title_sw)}
          </DialogTitle>
          <DialogDescription className="text-base mt-2">
            {t(currentStep.description_en, currentStep.description_sw)}
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="space-y-2 my-4">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{t('Step', 'Hatua')} {step + 1} / {steps.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 mb-4">
          {steps.map((_, i) => (
            <div 
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === step ? 'bg-primary' : i < step ? 'bg-primary/50' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          {currentStep.action && (
            <Button 
              className="w-full rounded-full"
              onClick={() => handleAction(currentStep.action)}
              data-testid="onboarding-action-btn"
            >
              {t(currentStep.actionLabel_en, currentStep.actionLabel_sw)}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          
          <div className="flex gap-2 w-full">
            {step < steps.length - 1 ? (
              <>
                <Button 
                  variant="ghost" 
                  className="flex-1"
                  onClick={handleSkip}
                  data-testid="onboarding-skip-btn"
                >
                  {t('Skip Tutorial', 'Ruka Mafunzo')}
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={handleNext}
                  data-testid="onboarding-next-btn"
                >
                  {t('Next', 'Endelea')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            ) : (
              <Button 
                className="w-full rounded-full"
                onClick={handleComplete}
                data-testid="onboarding-finish-btn"
              >
                {t('Start Investing!', 'Anza Kuwekeza!')}
                <TrendingUp className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Helper to reset onboarding (for testing)
export const resetOnboarding = () => {
  localStorage.removeItem(ONBOARDING_KEY);
};

// Helper to check if onboarding is completed
export const isOnboardingCompleted = () => {
  return localStorage.getItem(ONBOARDING_KEY) === 'true';
};
